import Quill from 'quill'
import { sanitizeOptions } from './utils/sanitize'
import { UserMentionBlot } from './blots/UserMention'

/* @ngInject */
function InputRichTextController ($scope, $attrs, $translate, $timeout, $window, $location, UdsModalService, UserService, CONSTANTS, TasksService, Utils, ToastService) {
  const getInputRichTextLinkDialogController = () => import('../../../core/dialogs/input-rich-text-link/input-rich-text-link-dialog.controller')
  const getTributeModule = () => import('tributejs')
  const getSanitizeHtmlModule = () => import('sanitize-html')

  const defaultTabType = 'public'
  const defaultMarkValue = false
  const defaultFocusedValue = false

  const defaultSendIcon = 'publish'
  const defaultSendText = ''
  const defaultSendHideValue = false

  const defaultUndoIcon = 'undo'
  const defaultUndoText = ''
  const defaultUndoHideValue = true

  let editorInputElement = null
  let inputRichTextContainer = null

  let editor = null
  let tribute = null

  $scope.flags = {
    disabled: $scope.disabled || false,
    focused: $scope.alwaysFocused || $scope.focused || defaultFocusedValue,
    markButton: {
      active: $scope.isMark || defaultMarkValue
    },
    sendButton: {
      hide: $scope.hideSend || defaultSendHideValue,
      icon: $scope.sendButtonIcon ?? defaultSendIcon,
      text: $scope.sendButtonText ?? defaultSendText,
      disabled: true,
      clearValue: $scope.onSendClearValue ?? true
    },
    undoButton: {
      hide: $scope.hideUndo ?? defaultUndoHideValue,
      icon: $scope.undoButtonIcon ?? defaultUndoIcon,
      text: $scope.undoButtonText ?? defaultUndoText,
      disabled: true
    }
  }

  $scope.previewLink = {
    show: false,
    top: '',
    left: '',
    marginTop: '4px',
    link: '',
    selectionRange: null
  }

  $scope.editor = {
    value: null,
    originalValue: null,
    hasMark: $scope.flags.markButton.active,
    type: $scope.type || defaultTabType,
    userIds: []
  }

  const api = {
    getInputRichTextLinkDialogController,
    getTributeModule,
    getSanitizeHtmlModule,
    _initInputRichText,
    _replaceDefaultIcons,
    _onEditorTextChange,
    _onEditorSelectionChange,
    _hasEditorText,
    _hasChanges,
    _handleLink,
    _onSaveLink,
    _onTooltipLinkOpen,
    _onTooltipLinkHide,
    _autoLink,
    _isWhitespace,
    _getPreviewInfo,
    _convertURL,
    _setSelectionIndexOnChange,
    _calculatePreviewPosition,
    _initTribute,
    _getUsersMention,
    _getMentionUser,
    _onSelectUser,
    _onRemoveUser,
    _setSelection,
    _handleRemoveMention,
    _handleTributeSelectOnEnter,
    _clearEditorValue,
    _onInputChange,
    _onEditorPaste,
    _isValidURL,
    _onEditorFocus,
    _onEditorBlur,
    _setValueToInput,
    _replaceDivs,
    _replaceCustomSymbols,
    _handleTributeValues
  }
  this.getApi = () => api

  activate()
  function activate () {
    api._initInputRichText()
    api._setValueToInput()
    api._initTribute()

    $scope.editor.originalValue = $scope.value

    editor.on('text-change', api._onEditorTextChange)
    editor.on('selection-change', api._onEditorSelectionChange)
  }

  const deregisterPlaceholderWatcher = $scope.$watch('placeholder', (newPlaceholder, oldPlaceholder) => {
    if (editor && newPlaceholder !== oldPlaceholder) {
      editor.root.dataset.placeholder = $scope.placeholder
    }
  })

  const deregisterTypeWatcher = $scope.$watch('type', (newValue) => {
    if (newValue) {
      $scope.editor.type = $scope.type
    }
  })

  const deregisterFocusedWatcher = $scope.$watch('focused', () => {
    if ($scope.focused) {
      $scope.flags.focused = $scope.focused
      editorInputElement.focus()
    }
  })

  const deregisterAlwaysFocusedWatcher = $scope.$watch('alwaysFocused', () => {
    if ($scope.alwaysFocused) {
      $scope.flags.focused = $scope.alwaysFocused
      editorInputElement.focus()
    }
  })

  const deregisterDisabledWatcher = $scope.$watch('disabled', (newValue) => {
    $scope.flags.disabled = newValue
    if (editor) {
      editor.enable(!newValue)
    }
  })

  $scope.$on('$destroy', () => {
    deregisterTypeWatcher()
    deregisterPlaceholderWatcher()
    deregisterFocusedWatcher()
    deregisterAlwaysFocusedWatcher()
    deregisterDisabledWatcher()
  })

  $scope.onMarkButton = () => {
    $scope.flags.markButton.active = !$scope.flags.markButton.active
    $scope.editor.hasMark = $scope.flags.markButton.active

    if ($scope.onMark) {
      $scope.onMark($scope.flags.markButton.active)
    }
  }

  $scope.onSendButton = () => {
    const editorValue = editor.getSemanticHTML()

    $scope.editor.value = editorValue
    $scope.editor.originalValue = editorValue
    if ($scope.onSend) {
      $scope.onSend($scope.editor)
      $scope.alwaysFocused = false

      if ($scope.flags.sendButton.clearValue) {
        api._clearEditorValue()
      }
    }
  }

  $scope.onUndoButton = () => {
    if ($scope.editor.originalValue) {
      editor.clipboard.dangerouslyPasteHTML($scope.editor.originalValue ?? '\n')
    }

    if (!$scope.editor.originalValue) {
      api._clearEditorValue()
    }

    $timeout(() => {
      $scope.flags.undoButton.disabled = true
      $scope.flags.sendButton.disabled = true
    })
  }

  $scope.onTabChange = (event) => {
    const tabType = event.detail?.selected || defaultTabType
    $scope.editor.type = tabType

    if (tribute?.isActive) {
      tribute.hideMenu()
      if (tribute.collection && tribute.collection[0]) {
        tribute.collection[0].values = []
      }
    }
    if ($scope.onTab) {
      $scope.onTab($scope.editor.type)
    }

    api._onInputChange()
  }

  $scope.onTabFocus = (event) => {
    const type = event.detail.name
    $scope.editor.type = type
    editorInputElement.focus()
  }

  $scope.openLink = () => {
    $scope.flags.focused = true
    $window.open($scope.previewLink.link, '_blank', 'noopener noreferrer')
  }

  $scope.editLink = api._handleLink

  $scope.isOnColor = () => {
    return $scope.theme === 'dark' && $scope.editor.type === 'public'
  }

  function _initInputRichText () {
    Quill.register(UserMentionBlot, true)

    inputRichTextContainer = $attrs.$$element.find('.input-rich-text-container')[0]
    const editorElement = inputRichTextContainer.querySelector('.input-rich-text__editor')

    const toolbarElement = inputRichTextContainer.querySelector('.input-rich-text-actions__toolbar')
    const defaultPlaceholder = $translate.instant('taskRateTextPlaceholder')
    const editorOptions = {
      modules: {
        toolbar: toolbarElement,
        keyboard: {
          bindings: {
            tributeSelectOnEnter: {
              key: 'Enter',
              handler: api._handleTributeSelectOnEnter
            }
          }
        }
      },
      formats: [
        'bold',
        'italic',
        'link',
        'strike',
        'userMention'
      ],
      placeholder: $scope.placeholder || defaultPlaceholder,
      theme: 'snow'
    }

    api._replaceDefaultIcons()

    editor = new Quill(editorElement, editorOptions)

    // $timeout(() => {
    //   $scope.flags.disabled = $scope.disabled || false
    //   editor.enable(!$scope.flags.disabled)
    // })

    const toolbar = editor.getModule('toolbar')

    editor.theme.tooltip.show = api._onTooltipLinkOpen
    editor.theme.tooltip.hide = api._onTooltipLinkHide

    editorInputElement = editorElement.querySelector('.ql-editor')

    editorInputElement.addEventListener('scroll', api._calculatePreviewPosition)
    editorInputElement.addEventListener('paste', api._onEditorPaste)
    editorInputElement.addEventListener('focus', api._onEditorFocus)
    editorInputElement.addEventListener('blur', api._onEditorBlur)

    toolbar.addHandler('link', api._handleLink)

    api._handleRemoveMention()

    $timeout(() => {
      const hasEditorText = api._hasEditorText()
      $scope.flags.sendButton.disabled = !hasEditorText
    })
  }

  function _replaceDefaultIcons () {
    const icons = Quill.import('ui/icons')

    const boldIcon = '<uds-icon name="bold" size="sm"></uds-icon>'
    const italicIcon = '<uds-icon name="italic" size="sm"></uds-icon>'
    const strikeIcon = '<uds-icon name="strikethrough" size="sm"></uds-icon>'
    const linkIcon = '<uds-icon name="link" size="sm"></uds-icon>'

    icons.bold = boldIcon
    icons.italic = italicIcon
    icons.strike = strikeIcon
    icons.link = linkIcon
  }

  function _hasEditorText () {
    const editorText = editor.getText().trim()
    const hasText = editorText.length > 0
    const hasMentions = $scope.editor.userIds.length > 0
    return hasText || hasMentions
  }

  function _hasChanges () {
    const editorText = editor.getText().trim()
    return editorText !== $scope.editor.originalValue
  }

  function _onEditorTextChange (delta, oldDelta, source) {
    $timeout(() => {
      const hasEditorText = api._hasEditorText()
      const isValueChanged = api._hasChanges()
      $scope.flags.sendButton.disabled = !hasEditorText || !isValueChanged
      $scope.flags.undoButton.disabled = !isValueChanged
    }, 500)

    api._autoLink(delta, oldDelta, source)
    api._setSelectionIndexOnChange()
    api._onInputChange()
  }

  function _onEditorSelectionChange (range) {
    if (range) {
      $scope.previewLink.selectionRange = range
    }

    api._calculatePreviewPosition()
  }

  function _handleLink () {
    const { selectionRange, linkText, linkFormat, isEditing } = api._getPreviewInfo()

    api.getInputRichTextLinkDialogController().then(({ InputRichTextLinkDialogController }) => {
      const inputRichTextLinkDialog = UdsModalService.createModal({
        controller: InputRichTextLinkDialogController,
        modalName: 'inputRichTextLinkDialog',
        data: {
          selectionRange,
          text: linkText,
          url: linkFormat,
          isEditing
        },
        onClose: (form) => api._onSaveLink(form, editor, isEditing)
      })
      inputRichTextLinkDialog.open()
    })
  }

  function _onSaveLink (form, editor, isEditing) {
    if (!form?.selectionRange) {
      return
    }

    const text = form.text
    const link = form.link
    const selectionRange = form.selectionRange

    if (isEditing) {
      if (!link) {
        editor.removeFormat(selectionRange.index, selectionRange.length)
        return
      }

      const isURL = api._isValidURL(text)

      if (isURL && text !== link) {
        editor.deleteText(selectionRange.index, selectionRange.length)
        editor.insertText(selectionRange.index, link)
        editor.formatText(selectionRange.index, link.length, 'link', link)
        return
      }

      editor.formatText(selectionRange.index, selectionRange.length, 'link', link)

      return
    }

    if (text) {
      editor.deleteText(selectionRange.index, selectionRange.length)
      editor.insertText(selectionRange.index, text)
      editor.formatText(selectionRange.index, text.length, 'link', link)

      if (!link) {
        editor.removeFormat(selectionRange.index, text.length)
      }
    }
  }

  function _onTooltipLinkOpen () {
    $timeout(() => {
      const { linkFormat } = api._getPreviewInfo()
      const tooltipElement = editor.theme.tooltip.root

      $scope.previewLink.top = tooltipElement.style.top

      $scope.previewLink.link = api._convertURL(linkFormat.link)

      $scope.previewLink.show = true
      $scope.flags.focused = true
    }, 100, true)
  }

  function _onTooltipLinkHide () {
    const selectionRange = editor.getSelection()

    if (selectionRange?.length) return

    $scope.previewLink.selectionRange = null

    $timeout(() => {
      $scope.previewLink.show = false
    })
  }

  function _autoLink (delta) {
    const regex = /https?:\/\/[^\s]+$/

    if (delta.ops.length === 2 && delta.ops[0].retain && api._isWhitespace(delta.ops[1].insert)) {
      const endRetain = delta.ops[0].retain
      const text = editor.getText().substr(0, endRetain)
      const match = text.match(regex)

      if (match !== null) {
        const url = match[0]
        let ops = []

        if (endRetain > url.length) {
          ops.push({ retain: endRetain - url.length })
        }

        ops = ops.concat([
          { delete: url.length },
          { insert: url, attributes: { link: url } }
        ])

        editor.updateContents({
          ops: ops
        })
      }
    }
  }

  function _isWhitespace (char) {
    const hasWhitespace = (char === ' ') || (char === '\t') || (char === '\n')

    return hasWhitespace
  }

  function _getPreviewInfo () {
    const range = $scope.previewLink.selectionRange || editor.getSelection()

    const [leaf] = editor.getLeaf(range.index)
    const leafIndex = editor.getIndex(leaf)

    const isSelection = !!range.length || !leaf.text

    const index = isSelection ? range.index : leafIndex
    const length = isSelection ? range.length : leaf.domNode.length

    const selectionRange = { index, length }

    const linkByElement = {
      link: leaf.parent.domNode.href
    }

    const linkText = editor.getText(selectionRange.index, selectionRange.length)
    const linkFormat = isSelection ? editor.getFormat(selectionRange.index, selectionRange.length) : linkByElement

    const isEditing = !!linkFormat?.link

    return {
      selectionRange,
      linkText,
      linkFormat,
      isEditing
    }
  }

  function _convertURL (url) {
    const isNotValidURL = !api._isValidURL(url)
    const prefix = 'https://'

    if (isNotValidURL) {
      return `${prefix}${url}`
    }

    return url
  }

  function _setSelectionIndexOnChange () {
    const range = editor.getSelection()

    if (!range?.length) {
      $scope.previewLink.selectionRange = range
    }
  }

  function _calculatePreviewPosition () {
    const tooltipElement = editor.theme.tooltip.root
    const hasNotPreviewLink = !$scope.previewLink.top

    if (hasNotPreviewLink) return

    $timeout(() => {
      const gap = 4
      const [marginTop] = tooltipElement.style.marginTop.split('px')

      const parsedMarginTop = parseFloat(marginTop)
      const isNegative = Math.sign(parsedMarginTop) === -1

      $scope.previewLink.top = tooltipElement.style.top

      if (parsedMarginTop) {
        const newMarginTop = isNegative ? parsedMarginTop + gap : parsedMarginTop - gap
        $scope.previewLink.marginTop = `${newMarginTop}px`
        return
      }

      $scope.previewLink.marginTop = `${gap}px`
    })
  }

  async function _handleTributeValues (text, callbackHandler) {
    try {
      const query = (text || '').trim()
      const mentionableUsers = await api._getUsersMention({ search: query })
      if ($scope.editor.type === 'private') {
        const filteredMentionableUsers = mentionableUsers.filter(user => user.role !== 'USER')
        callbackHandler(filteredMentionableUsers)
        return
      }
      callbackHandler(mentionableUsers)
    } catch (error) {
      console.error('Error fetching mentionable users:', error)
      ToastService.showErrorToast($translate.instant('somethingWentWrongTryAgainMsg'))
      callbackHandler([])
    }
  }

  function _initTribute (users = []) {
    api.getTributeModule().then((TributeModule) => {
      const TributeDefault = TributeModule.default

      tribute = new TributeDefault({
        values: api._handleTributeValues,
        lookup: 'name',
        searchOpts: {
          skip: true
        },
        noMatchTemplate: function () {
          const translatedText = $translate.instant('noResults')
          return `<div class='empty-result-content'>
            <uds-label class="truncated-text" size="md" font-weight="medium" title="${translatedText}">${translatedText}</uds-label>
          </div>`
        },
        menuShowMinLength: 0,
        menuItemTemplate: (user) => {
          const { avatar, userName } = api._getMentionUser(user)
          return `
            <div class="annotation-user-item">
              ${avatar}
              <div class="user-info">
                ${userName}
              </div>
            </div>
          `
        },
        selectTemplate: api._setSelection
      })

      tribute.attach(editorInputElement)
    })
  }

  function _getUsersMention (input = {}) {
    const { search } = input
    const urlParams = $location.search()

    if (urlParams?.request) {
      return TasksService.getMentionableUsersByTask({ id: urlParams.request, search })
    } else {
      return UserService.getUsersMention({ search })
    }
  }

  function _getMentionUser (user) {
    const name = user.original.name
    const id = user.original.id
    const profileImage = user.original.profileImageId

    const avatarSize = 'md'
    const avatarUrl = UserService.getUserProfileImage(id)

    const avatarImage = `<uds-avatar image-url="${avatarUrl}" size="${avatarSize}"></uds-avatar>`
    const avatarName = `<uds-avatar name="${name}" size="${avatarSize}"></uds-avatar>`

    const avatar = profileImage ? avatarImage : avatarName
    const userName = `<uds-label class="truncated-text" size="md" font-weight="medium" title="${name}">${name}</uds-label>`

    return {
      avatar,
      userName
    }
  }

  function _onSelectUser (user) {
    $scope.editor.userIds.push(user.original.id)
  }

  function _onRemoveUser (user) {
    const index = $scope.editor.userIds.indexOf(user.original.id)

    if (index >= 0) {
      $scope.editor.userIds.splice(index, 1)
    }
  }

  function _setSelection (user) {
    const range = editor.getSelection(true)
    const prefixAtLength = 1

    const mentionData = {
      userId: user.original.id,
      title: user.original.email,
      name: `@${user.original.name}`
    }

    editor.insertEmbed(range.index, 'userMention', mentionData)

    $timeout(() => {
      const newSelectionIndex = range.index + mentionData.name.length + prefixAtLength

      editor.setSelection(newSelectionIndex)
    }, 50, false)

    return ''
  }

  function _handleRemoveMention () {
    editorInputElement.addEventListener('tribute-replaced', (event) => {
      const user = event.detail.item
      const userId = user.original.id
      const mentionElement = editorInputElement.querySelector(`[data-user-id="${userId}"]`)

      const mutationConfig = {
        childList: true,
        subtree: true
      }

      const observer = new MutationObserver((mutations) => {
        const isInEditor = editorInputElement.contains(mentionElement)
        const hasUserId = $scope.editor.userIds.includes(userId)

        if (isInEditor && !hasUserId) {
          api._onSelectUser(user)
        }

        if (!isInEditor && hasUserId) {
          api._onRemoveUser(user)
        }
      })

      observer.observe(editorInputElement, mutationConfig)
    })
  }

  function _handleTributeSelectOnEnter (event) {
    if (tribute?.isActive) {
      tribute.selectItemAtIndex(tribute.menuSelected, event)
      tribute.hideMenu()
      return false
    }

    return true
  }

  function _clearEditorValue () {
    editor.setContents([{ insert: '\n' }])
  }

  function _onInputChange () {
    if ($scope.onChange) {
      const editorValue = editor.getSemanticHTML()

      $scope.onChange({
        ...$scope.editor,
        value: editorValue
      })
    }
  }

  function _onEditorPaste () {
    const selection = editor.getSelection()
    const whiteSpace = ' '
    editor.insertText(selection.index, whiteSpace)
  }

  function _isValidURL (url) {
    if (!url) return false

    const regex = /^[a-zA-Z]+:\/\//
    return url.match(regex) !== null
  }

  function _onEditorFocus () {
    $scope.flags.focused = true

    const editorElement = $attrs.$$element.find('.input-rich-text__editor')[0]
    editorInputElement = editorElement.querySelector('.ql-editor')

    // Necessário para garantir foco correto quando há apenas menções de usuário
    $timeout(() => {
      const length = editor.getLength()
      editor.setSelection(length - 1, 0)
      editorInputElement.focus()
    })
  }

  function _onEditorBlur () {
    if ($scope.alwaysFocused) return

    $scope.flags.focused = false
  }

  function _setValueToInput () {
    api.getSanitizeHtmlModule().then((SanitizeHtmlModule) => {
      const sanitizeHtml = SanitizeHtmlModule.default

      if ($scope.value) {
        let html = $scope.value

        // Avoid focus trigger on start.
        editor.enable(false)

        html = sanitizeHtml(html, sanitizeOptions)
        html = Utils.removeNBSPSymbol(html)
        html = api._replaceDivs(html)
        html = api._replaceCustomSymbols(html)

        editor.clipboard.dangerouslyPasteHTML(html)

        editor.enable(true)
      }
    })
  }
  function _replaceDivs (html) {
    const cleanedHtml = html.replaceAll('<div>', '<p>').replaceAll('</div>', '</p>')

    return cleanedHtml
  }

  function _replaceCustomSymbols (html) {
    const breakLine = '\n'
    const slash = '/'
    const chevronLeft = '<'
    const chevronRight = '>'

    const cleanedHtml = html.replaceAll('<br>', breakLine)
      .replace(/\\\\/g, slash)
      .replace(/&lt;/g, chevronLeft)
      .replace(/&gt;/g, chevronRight)

    return cleanedHtml
  }
}

export { InputRichTextController }
